// Shared workspace section nav — the workspace is merged into the Home feed, so each key is a
// "section" surfaced in the URL as /home?section=<key> (the Home hub = no param). Used by the Home
// page's right-nav, its embedded section renderer, and the header's mobile "Workspace" menu.

export type NavKey =
  | "dashboard"
  | "archives"
  | "shared"
  | "defaults"
  | "contributions"
  | "bookmarks"
  | "suggestions"
  | "teams";

export const NAV: { key: NavKey; label: string; icon: React.ReactNode }[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: (
      <>
        <rect x="3" y="3" width="7" height="9" rx="1" />
        <rect x="14" y="3" width="7" height="5" rx="1" />
        <rect x="14" y="12" width="7" height="9" rx="1" />
        <rect x="3" y="16" width="7" height="5" rx="1" />
      </>
    ),
  },
  {
    key: "shared",
    label: "Shared with me",
    icon: (
      <>
        <circle cx="18" cy="5" r="3" />
        <circle cx="6" cy="12" r="3" />
        <circle cx="18" cy="19" r="3" />
        <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" />
      </>
    ),
  },
  {
    key: "defaults",
    label: "Default skills",
    icon: (
      <>
        <path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6z" />
        <path d="M9 12l2 2 4-4" />
      </>
    ),
  },
  {
    key: "contributions",
    label: "Contributions",
    icon: (
      <>
        <path d="M12 2v20M2 12h20" />
        <circle cx="12" cy="12" r="9" />
      </>
    ),
  },
  {
    key: "bookmarks",
    label: "Bookmarks",
    icon: <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />,
  },
  {
    key: "suggestions",
    label: "Skill suggestions",
    icon: (
      <>
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </>
    ),
  },
  {
    key: "teams",
    label: "Teams",
    icon: (
      <>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </>
    ),
  },
  {
    key: "archives",
    label: "Archives",
    icon: (
      <>
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <path d="M12 9v4M12 17h.01" />
      </>
    ),
  },
];

// Route → the URL for a section. The workspace was merged into the Home feed, so sections live at
// /home?section=<key> (the "dashboard" key is just the Home hub, no param).
export const sectionHref = (key: NavKey) => (key === "dashboard" ? "/home" : `/home?section=${key}`);

// The workspace quick-links, shared by the Home page's right-hand nav (desktop) and the header's
// "Workspace" menu (mobile). `section` is the ?section= value used to highlight the active item
// (null for links that leave Home, e.g. My skills → Explore, or the Home hub itself).
export const WORKSPACE_LINKS: { label: string; to: string; section: NavKey | null }[] = [
  { label: "Home", to: "/home", section: null },
  { label: "My skills", to: "/skills?tab=mine", section: null },
  { label: "Shared with me", to: "/home?section=shared", section: "shared" },
  { label: "Default skills", to: "/home?section=defaults", section: "defaults" },
  { label: "Skill suggestions", to: "/home?section=suggestions", section: "suggestions" },
  { label: "My contributions", to: "/home?section=contributions", section: "contributions" },
  { label: "Teams", to: "/home?section=teams", section: "teams" },
  { label: "Bookmarks", to: "/home?section=bookmarks", section: "bookmarks" },
  { label: "Archives", to: "/home?section=archives", section: "archives" },
];
